import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../../services/AuthContext";

import MuiAppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import { 
    Badge, 
    Button, 
    Menu, 
    MenuItem, 
    styled, 
    Tooltip, 
    AppBarProps as MuiAppBarProps, 
    Divider, 
    ListItemText, 
    ListItemIcon, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogContentText, 
    DialogActions, 
    Switch,
    Container,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { AccountCircle, ConnectWithoutContact } from "@mui/icons-material";
import MailIcon from '@mui/icons-material/Mail';
import LogoutIcon from '@mui/icons-material/Logout';
import LockIcon from '@mui/icons-material/Lock';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

import { BaseControlProps } from "../../models/BaseControlProps";
import { ReactComponent as SvgLogo } from "./logo.svg";
import "./Header.css";
import { MessageAPIService } from "../../services/MessageAPIService";
import { MessageGetRequest } from "../../models/MessageGetRequest";
import MessageDataTable from "../MessageDataTable/MessageDataTable";
import { Message } from "../../models/Message";
import { useMessage } from "../Common/MessageContext/MessageContext";

const drawerWidthClosed = 72;
const smallerdrawerWidthClosed = 56;

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
    drawerWidth: number;
}

interface HeaderProps extends BaseControlProps {
    drawerWidth: number;
    open: boolean;
    setOpen: (open: boolean) => void;
    toggleDrawer: () => void;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
  })<AppBarProps>(({ theme, open, ...props }) => ({
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: 'white',
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(!open && {
        width: `calc(100% - ${smallerdrawerWidthClosed}px)`, // Default closed width (matches closed drawer)
        [theme.breakpoints.up('sm')]: {
        width: `calc(100% - ${drawerWidthClosed}px)`, // Slightly wider on larger screens
        },
        //maxWidth: theme.breakpoints.values.md, // Match footer's md breakpoint
    }),
    ...(open && {
      marginLeft: props.drawerWidth,
      width: `calc(100% - ${props.drawerWidth}px)`,
      backgroundColor: 'white',
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  }));

export function Header(props: HeaderProps) {
    const pageSize = 50;
    const loginContext = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [anchorElContact, setAnchorElContact] = React.useState<null | HTMLElement>(null);
    const authContext = useAuth();
    const [scopeGet, applyGet] = useState<MessageGetRequest>({
        pageSize: pageSize,
        pageNo: 0,
        filterSearchString: "",
        orderByString: ""
    });
    const [messageCount, setMessageCount] = useState<number>(0);
    const [messageBoxOpen, setMessageBoxOpen] = React.useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [paperPayslipDisabled, setPaperPayslipDisabled] = useState(true);
    const { showMessage } = useMessage();
    const isAdmin = loginContext ? loginContext.isAdmin : false;
    const messageApiService = new MessageAPIService();
    const [payslipType, setPayslipType] = useState<string | null>(localStorage.getItem('sendPaperPaySlip'));
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // 'sm' for small screens

    useEffect(() => {
        const getNewMessages = async () => {
            await getAllNewMessages();

        }

        getNewMessages();
        authContext.checkTokenExpiry();
    }, []);

    useEffect(() => {
        if (messageBoxOpen) {
            setTimeout(() => {
            const focusable = document.getElementById('scroll-dialog-description');
            focusable?.focus();
            }, 100);
        }
    }, [messageBoxOpen]);

    const getAllNewMessages = async () => {
        messageApiService.getNewMessages(scopeGet, authContext.token).then((response) => {
            if (response.error) {
                showMessage(response.error, 'error');
            }
            else {
                const count = response.response?.messages ? response.response?.messages.length : 0; 
                setMessageCount(count);
                if (count > 0 && loginContext.showMessages === 'true') {
                    setMessageBoxOpen(true);
                    loginContext.messagesDisplayed();
                }
                const dataMessages = response.response?.messages ? response.response?.messages : [];
                setMessages(dataMessages);
            }
        });
    }

    const handleLogout = () => {
        loginContext.logout();
    }

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuContact = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElContact(event.currentTarget);
    }

    const handleMenuContactClose = () => {
        setAnchorElContact(null);
    }

    const handleMessageBoxClose = () => {
        setMessageBoxOpen(false);
    };

    const handleClickOnSecurity = () => {
        handleClose();
        navigate('/security');
    }

    const handleClickOnChangeEmail = () => {
        handleClose();
        navigate('/change-email');
    }

    const handleClickOnPaperPayslip = () => {
        handleClose();
        navigate('/paper-payslip');
    }

    const handleContactUsClick = () => {
        window.open('https://www.justaskmercer.com', '_blank', 'noopener,noreferrer');
    };

    return (
        <>
            <AppBar position="absolute" open={props.open} drawerWidth={props.drawerWidth}>
                <Container maxWidth="lg" sx={{ p: 0 }}>
                    <Toolbar 
                        sx={{
                            pr: '24px', // keep right padding when drawer closed
                            color: '#616161',
                            ml: '20px'
                        }}
                    >
                        
                        <div className="logo-container">
                            <SvgLogo focusable={false} viewBox="0 0 500 150" />
                        </div>
                        <Typography
                            component="h1"
                            variant="h6"
                            color="inherit"
                            noWrap
                            sx={{ flexGrow: 1 }}
                            >
                                
                        </Typography>
                        { isAdmin === 'false' ? (
                            <>
                                {isMobile ? (
                                    <Button 
                                        id="menu-contactus"
                                        color="secondary" 
                                        onClick={handleMenuContact} sx={{
                                            minWidth: 'auto',
                                            padding: '8px',
                                        }}
                                        aria-label="Contact Mercer"
                                        aria-controls="menu-contactus"
                                        aria-haspopup="true"
                                    >
                                        <PhoneIcon />
                                    </Button>
                                ) : (
                                    <Button 
                                        id="button-contactus"
                                        color="secondary"  
                                        onClick={handleMenuContact}
                                        aria-controls="menu-contactus"
                                        aria-haspopup="true"
                                    >
                                        Contact Us
                                    </Button>
                                )}

                                <Menu
                                    id="menu-contactus"
                                    anchorEl={anchorElContact}
                                    open={Boolean(anchorElContact)}
                                    onClose={handleMenuContactClose}
                                >
                                    <MenuItem onClick={() => {
                                        if (isMobile) {
                                            window.location.href = 'tel:+35315255339';
                                        } else {
                                            navigator.clipboard.writeText('+353 1 525 5339');
                                        }
                                    }}>
                                        <ListItemIcon sx={{ color: 'primary.main', minWidth: '40px' }}>
                                            <PhoneIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary="+353 1 525 5339" 
                                            primaryTypographyProps={{
                                                fontWeight: 500,
                                                color: 'text.primary',
                                                textAlign: 'center'
                                            }}
                                            />
                                            {!isMobile && (
                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                Click to copy
                                            </Typography>
                                            )}
                                    </MenuItem>
                                    <MenuItem 
                                        disabled 
                                        sx={{
                                        padding: '8px 16px',
                                        opacity: 1, // Override disabled opacity
                                        cursor: 'default'
                                        }}
                                    >
                                        <ListItemText
                                            primary="Available from 9am to 5pm"
                                            secondary="Monday to Friday (excluding bank holidays)"
                                            primaryTypographyProps={{
                                                fontSize: '0.8rem',
                                                textAlign: 'center'
                                            }}
                                            secondaryTypographyProps={{
                                                fontSize: '0.75rem',
                                                textAlign: 'center'
                                            }}
                                            sx={{
                                                my: 0.5
                                            }}
                                        />
                                    </MenuItem>
                                    <Divider sx={{ my: 0.5 }} />
                                    <MenuItem 
                                        onClick={handleContactUsClick}
                                        sx={{
                                        textAlign: 'center',
                                        padding: '12px 16px'
                                        }}
                                    >
                                        <ListItemText 
                                            primary="Contact Us" 
                                        />
                                    </MenuItem>
                                </Menu>
                            </>
                        ) : ""}
                        
                        { isAdmin === 'false' ? (
                            <IconButton sx={{ color: 'primary.main' }} size="large" aria-label="show new mails" onClick={() => setMessageBoxOpen(true) } color="inherit">
                                <Badge  sx={{ color: 'inherit' }} badgeContent={messageCount} color="error">
                                    <MailIcon />
                                </Badge>
                            </IconButton>
                        ) : ""}

                        <Tooltip title="Open settings">
                            <IconButton
                                size="large"
                                aria-label="account of current user"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={handleMenu}
                                color="inherit"
                                sx={{ color: 'primary.main' }}
                            >
                                <AccountCircle  />
                            </IconButton>
                        </Tooltip>

                        <Menu
                            sx={{ mt: '45px' }}
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                        >
                            <MenuItem disabled>
                                <ListItemIcon sx={{ color: 'inherit' }}>
                                    <AccountCircle fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{loginContext.userName}</ListItemText>
                            </MenuItem>
                            <Divider />
                            { isAdmin === 'false' ? (
                                <>
                                    <MenuItem disabled className="text-center">
                                        <ListItemText>Account</ListItemText>
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem onClick={handleClickOnSecurity}>
                                        <ListItemIcon sx={{ color: 'inherit' }}>
                                            <LockIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>Change Password</ListItemText>
                                    </MenuItem>
                                    <MenuItem onClick={handleClickOnPaperPayslip}>
                                        <ListItemIcon sx={{ color: 'inherit' }}>
                                            <ForwardToInboxIcon />
                                        </ListItemIcon>
                                        <ListItemText id="payslip-switch-label">Send Paper Payslip</ListItemText>
                                        {/* <Switch
                                            checked={payslipType === 'true' ? true : false}
                                            disabled={paperPayslipDisabled}
                                            inputProps={{ 'aria-label': 'Paper payslips', 'aria-labelledby': 'payslip-switch-label', }}
                                        /> */}
                                    </MenuItem>
                                    <MenuItem onClick={handleClickOnChangeEmail}>
                                        <ListItemIcon sx={{ color: 'inherit' }}>
                                            <EmailIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>Change Email</ListItemText>
                                    </MenuItem>
                                    <Divider />
                                </>
                            ) : ""}
                            <MenuItem onClick={handleLogout}>
                                <ListItemIcon sx={{ color: 'inherit' }}>
                                    <LogoutIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Logout</ListItemText>
                            </MenuItem>
                        </Menu>
                        <Dialog
                            open={messageBoxOpen}
                            onClose={handleMessageBoxClose}
                            scroll="paper"
                            aria-labelledby="scroll-dialog-title"
                            aria-describedby="scroll-dialog-description"
                            fullWidth={true}
                            maxWidth="md"
                        >
                            <DialogTitle id="scroll-dialog-title">
                                You have {messageCount} new messages!
                            </DialogTitle>
                            <DialogContent dividers={true}>
                                <DialogContentText
                                    id="scroll-dialog-description"
                                    tabIndex={-1}
                                >
                                    <MessageDataTable 
                                        loading={loading} 
                                        data={messages} 
                                        userId={loginContext.userId} 
                                        setData={setMessages}
                                        messageCount={messageCount}
                                        setMessageCount={setMessageCount}
                                    />
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                            <Button onClick={handleMessageBoxClose}>CLOSE</Button>
                            </DialogActions>
                        </Dialog>
                    </Toolbar>
                </Container>
            </AppBar>
        </>

    )
}